/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect, useReducer, useCallback } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';

function reducer(state, action) {
  switch (action.type) {
    case 'changed_autopayInfo': {
      return {
        ...action.autopayInfo,
      };
    }
    case 'changed_property': {
      return {
        ...state,
        ...action.property,
      };
    }
  }
  throw Error('Unkown action: ' + action.type);
}

const useGetAutopayInfo = (loanNumber) => {
  const { get } = useAbortController();
  const { token } = useAuthContext();

  const [autopayInfo, dispatchAutopayInfo] = useReducer(reducer, {
    authorizationDate: null,
    eftRoutingNo: '',
    additionalPrincipal: 0,
    additionalEscrow: 0,
    eftBankAccountNo: '',
    eftBankName: '',
    startDate: null,
    eftBankAccountTypeKeyword: '',
    authorizationMethodKeyword: '',
    autoPayStatusKeyword: '',
    settleDay: '',
    otpRoutingNo: '',
    otpBankAccountNo: '',
    otpBankName: '',
    otpBankAccountTypeKeyword: '',
  });
  const [autopayStatus, setAutopayStatus] = useState('done');

  const getAutopayInfo = useCallback(async () => {
    if (!token || !loanNumber) return;
    const controller = get();
    const config = { signal: controller.signal };
    const headers = {
      'Session-Token': token,
    };
    try {
      setAutopayStatus('loading');
      let response = await axiosCall({ url: `live/loan/${loanNumber}/servicing`, method: 'get' }, config, headers);
      dispatchAutopayInfo({ type: 'changed_autopayInfo', autopayInfo: response });
      setAutopayStatus('done');
    } catch (e) {
      if (e.code !== 'ERR_CANCELED') {
        console.error('Unable to load autopay info: ' + e);
        setAutopayStatus('error');
      }
    }
  }, [get, loanNumber, token]);

  useEffect(() => {
    getAutopayInfo();
  }, [getAutopayInfo]);

  return {
    autopayInfo,
    autopayStatus,
    dispatchAutopayInfo,
    refetchAutopayInfo: getAutopayInfo,
  };
};

export default useGetAutopayInfo;
